import { lazy, useState } from 'react'
import { MARKET_CAP_POLLING_PERIOD, useMarketCapRanking } from '@hooks/useMarketCapRanking.js'
import { TRADING_SIGNAL_POLLING_PERIOD, useTradingSignal } from '@hooks/useTradingSignal.js'
import { useHomeCombinedAssetIds } from '@hooks/useHomeCombinedAssetIds.js'
import { useCurrentPrice } from '@hooks/useCurrentPrice.js'
import { useMStockPolling } from '@hooks/useMStockPolling.js'
import { getOrderedCategories } from '@constants/categories.js'
import { MSTOCK_SCROLL_KEY, useScrollMemory } from '@hooks/useScrollMemory.js'

const MarketTrendSection = lazy(() => import('@sections/mStock/home/MarketTrendSection.jsx'))
const MarketCapSection = lazy(() => import('@/sections/mStock/home/MarketCapSection.jsx'))
const TradingSignalSection = lazy(() => import('@/sections/mStock/home/TradingSignalSection.jsx'))
const KimchiPremiumSection = lazy(() => import('@/sections/mStock/home/KimchiPremiumSection.jsx'))
const EtfNetSection = lazy(() => import('@/sections/mStock/home/EtfNetSection.jsx'))
const NewsSection = lazy(() => import('@/sections/mStock/home/NewsSection.jsx'))

const EXCHANGE_TABS = getOrderedCategories(__APP_TARGET__)

const MStockHomePage = () => {
  const [activeExchange, setActiveExchange] = useState(EXCHANGE_TABS[0].key)

  const {
    data: marketCapData,
    lastFetchTime: marketCapLastFetchTime,
    isLoading: isMarketCapLoading,
    isError: isMarketCapError,
    refetchAll: refetchMarketCapAll,
    refetchMarketCap
  } = useMarketCapRanking()

  const {
    data: tradingSignals,
    exchanges: signalExchanges,
    lastFetchTime: signalLastFetchTime,
    isLoading: isSignalLoading,
    isError: isSignalError,
    refetchAll: refetchTradingSignalAll,
    refetchTradingSignal
  } = useTradingSignal({ activeExchange })

  const combinedAssetIds = useHomeCombinedAssetIds({
    marketCapData,
    signalExchanges,
    enabled: !isMarketCapLoading && !isSignalLoading
  })

  const {
    prices: currentPrices,
    isLoading: isCurrentPriceLoading,
    isError: isCurrentPriceError,
    refetch: refetchCurrentPrice
  } = useCurrentPrice(combinedAssetIds)

  useMStockPolling(refetchMarketCap, MARKET_CAP_POLLING_PERIOD)
  useMStockPolling(refetchTradingSignal, TRADING_SIGNAL_POLLING_PERIOD)

  const saveScroll = useScrollMemory(MSTOCK_SCROLL_KEY.HOME, {
    isReady: !isMarketCapLoading && !isSignalLoading
  })

  return (
    <>
      <div className="text-m-content-primary px-16" onClickCapture={saveScroll}>
        <MarketTrendSection
          marketCapData={marketCapData}
          isMarketCapLoading={isMarketCapLoading}
          isMarketCapError={isMarketCapError}
          onRetryMarketCap={refetchMarketCapAll}
          currentPrices={currentPrices}
          isCurrentPriceLoading={isCurrentPriceLoading}
          isCurrentPriceError={isCurrentPriceError}
          onRetryCurrentPrice={refetchCurrentPrice}
        />
        <MarketCapSection
          data={marketCapData}
          lastFetchTime={marketCapLastFetchTime}
          isLoading={isMarketCapLoading}
          isError={isMarketCapError}
          onRetry={refetchMarketCapAll}
          currentPrices={currentPrices}
        />
        <TradingSignalSection
          data={tradingSignals}
          lastFetchTime={signalLastFetchTime}
          isLoading={isSignalLoading}
          isError={isSignalError}
          onRetry={refetchTradingSignalAll}
          currentPrices={currentPrices}
          tabs={EXCHANGE_TABS}
          activeExchange={activeExchange}
          setActiveExchange={setActiveExchange}
        />
        <KimchiPremiumSection />
        <EtfNetSection />
        <NewsSection />
      </div>
    </>
  )
}

export default MStockHomePage
